import React, { useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import ErrorMessage from "../components/ErrorMessage";
import Topbar from "../components/Topbar";
import "./dashboard.css";

const COLORS_PIE = ["#1cc400", "#e0e0e0"];
const COLORS_BAR = ["#1cc400", "#0b4e00", "#4ade80", "#bbf7d0", "#86efac"];
const COLORS_STATUS = {
  pending:       "#f59e0b",
  requested:     "#f59e0b",
  active:        "#1cc400",
  completed:     "#3b82f6",
  cancelled:     "#ef4444",
  inprogress:    "#8b5cf6",
  "in-progress": "#8b5cf6",
  "in progress": "#8b5cf6",
};

const StatCard = ({ label, value, loading, accent }) => (
  <div className={`dash-stat-card${accent ? " dash-stat-card--accent" : ""}`}>
    <span className="dash-stat-label">{label}</span>
    {loading ? (
      <span className="dash-skeleton dash-skeleton--value" />
    ) : (
      <strong className="dash-stat-value">{value ?? 0}</strong>
    )}
  </div>
);

const ChartCard = ({ title, loading, children }) => (
  <div className="dash-chart-card">
    <p className="dash-chart-title">{title}</p>
    {loading ? (
      <div className="dash-chart-loading">
        <span className="dash-spinner" />
        <span className="dash-chart-loading-text">Loading…</span>
      </div>
    ) : (
      children
    )}
  </div>
);

const EMPTY_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const toBarData = (chart) => {
  if (!chart?.labels?.length) return [{ name: "No data", value: 0 }];
  return chart.labels.map((label, i) => ({ name: label, value: chart.data?.[i] ?? 0 }));
};

const toPieData = (chart) => {
  if (!chart?.labels?.length) return [{ name: "No data", value: 1 }];
  return chart.labels.map((label, i) => ({ name: label, value: chart.data?.[i] ?? 0 }));
};

const toAreaData = (chart) => {
  if (!chart?.labels?.length)
    return EMPTY_MONTHS.map((m) => ({ month: m, count: 0 }));
  return chart.labels.map((label, i) => ({ month: label, count: chart.data?.[i] ?? 0 }));
};

const statusColor = (name) =>
  COLORS_STATUS[name?.toLowerCase?.()] || COLORS_BAR[0];

const { RangePicker } = DatePicker;

const Dashboard = ({ users, admins, stats, dashboard, loading, error, onRefresh }) => {
  const defaultFrom = dashboard?.filter?.dateFrom
    ? dayjs(dashboard.filter.dateFrom)
    : dayjs().subtract(1, "year");
  const defaultTo = dashboard?.filter?.dateTo
    ? dayjs(dashboard.filter.dateTo)
    : dayjs();

  const [range, setRange] = useState([defaultFrom, defaultTo]);

  const { summary = {}, charts = {} } = dashboard || {};

  const userGrowthData         = toAreaData(charts.userGrowth);
  const taskGrowthData         = toAreaData(charts.taskGrowth);
  const tasksByStatusData      = toBarData(charts.tasksByStatus);
  const reviewsByRatingData    = toBarData(charts.reviewsByRating);
  const verifiedData           = toPieData(charts.verifiedVsUnverified);
  const serviceRegData         = toPieData(charts.serviceRegistration);

  const handleRangeChange = (val) => {
    if (val?.[0] && val?.[1]) setRange(val);
  };

  const handleOpenChange = (open) => {
    if (!open && range?.[0] && range?.[1]) {
      onRefresh({
        dateFrom: range[0].format("YYYY-MM-DD"),
        dateTo:   range[1].format("YYYY-MM-DD"),
      });
    }
  };

  return (
    <>
      <Topbar title="Dashboard" subtitle="Platform overview and analytics.">
        <div className="dash-filter-row">
          <RangePicker
            value={range}
            onChange={handleRangeChange}
            onOpenChange={handleOpenChange}
            disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
            format="YYYY-MM-DD"
            allowClear={false}
          />
        </div>
      </Topbar>

      <ErrorMessage message={error} />

      {/* ── Summary Cards ── */}
      <div className="dash-stats-grid">
        <StatCard label="Total Users"      value={summary.totalUsers}    loading={loading} accent />
        <StatCard label="Total Admins"     value={summary.totalAdmins}   loading={loading} />
        <StatCard label="Verified Users"   value={summary.verifiedUsers} loading={loading} />
        <StatCard label="Unverified Users" value={summary.unverifiedUsers} loading={loading} />
        <StatCard label="Total Tasks"      value={summary.totalTasks}    loading={loading} />
        <StatCard label="Total Services"   value={summary.totalServices} loading={loading} />
        <StatCard label="Total Reviews"    value={summary.totalReviews}  loading={loading} />
      </div>

      {/* ── Charts Row 1: Area charts ── */}
      <div className="dash-charts-row">
        <ChartCard title="User Growth (by month)" loading={loading}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={userGrowthData} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#1cc400" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#1cc400" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="count"
                name="Users"
                stroke="#1cc400"
                strokeWidth={2}
                fill="url(#colorUsers)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Task Growth (by month)" loading={loading}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={taskGrowthData} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorTasks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="count"
                name="Tasks"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#colorTasks)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Charts Row 2: Bar charts ── */}
      <div className="dash-charts-row">
        <ChartCard title="Tasks by Status" loading={loading}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tasksByStatusData} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" name="Tasks" radius={[4, 4, 0, 0]}>
                {tasksByStatusData.map((entry, index) => (
                  <Cell key={index} fill={statusColor(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Reviews by Rating" loading={loading}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={reviewsByRatingData} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" name="Reviews" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Charts Row 3: Pie charts ── */}
      <div className="dash-charts-row dash-charts-row--pie">
        <ChartCard title="Verified vs Unverified Users" loading={loading}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={verifiedData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
              >
                {verifiedData.map((_, i) => (
                  <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend iconType="circle" iconSize={10} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Service Registered vs Not Registered" loading={loading}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={serviceRegData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
              >
                {serviceRegData.map((_, i) => (
                  <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend iconType="circle" iconSize={10} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
};

export default Dashboard;
